// FE-SCRM-050 — AddContactModal launched from the Sales Contacts
// section of the Prospect detail page.
//
// Mirrors the FE-SCRM-049 modal pattern the Prospects list will
// reuse for ``+ Add prospect`` (first name + last name required,
// email / phone / title optional, is_primary toggle). On submit
// the host invalidates the contacts query so the new row appears
// in the table without a refetch.

import { useEffect, useState } from "react";

import { Modal } from "@/design-system";

import { CrmFormBanner } from "../components";

import type { SalesContactCreateInput } from "./types";


interface Props {
  open: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (input: SalesContactCreateInput) => void;
}


export function AddContactModal({
  open, isSubmitting, errorMessage, onClose, onSubmit,
}: Props) {
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [is_primary, setIsPrimary] = useState(false);
  const [touched, setTouched] = useState(false);

  // Reset whenever the modal opens. Closing without submitting
  // should not preserve half-filled fields.
  useEffect(() => {
    if (!open) return;
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setTitle("");
    setIsPrimary(false);
    setTouched(false);
  }, [open]);

  const fieldErrors: Record<string, string> = {};
  if (touched) {
    if (!first_name.trim()) fieldErrors.first_name = "First name is required.";
    if (!last_name.trim()) fieldErrors.last_name = "Last name is required.";
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!first_name.trim() || !last_name.trim()) return;
    onSubmit({
      first_name: first_name.trim(),
      last_name: last_name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      title: title.trim() || null,
      is_primary,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add contact"
    >
      <form
        onSubmit={submit}
        className="flex flex-col gap-3"
        data-testid="add-contact-form"
      >
        <CrmFormBanner
          errors={[
            ...(errorMessage ? [{ message: errorMessage }] : []),
            ...Object.entries(fieldErrors).map(([field, message]) => ({
              field, message,
            })),
          ]}
        />


        <Field id="contact-first-name" label="First name" required>
          <input
            id="contact-first-name"
            data-testid="add-contact-first-name"
            value={first_name}
            onChange={(e) => setFirstName(e.target.value)}
            className={inputClass(Boolean(fieldErrors.first_name))}
            autoFocus
          />
        </Field>

        <Field id="contact-last-name" label="Last name" required>
          <input
            id="contact-last-name"
            data-testid="add-contact-last-name"
            value={last_name}
            onChange={(e) => setLastName(e.target.value)}
            className={inputClass(Boolean(fieldErrors.last_name))}
          />
        </Field>

        <Field id="contact-email" label="Email">
          <input
            id="contact-email"
            data-testid="add-contact-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass(false)}
          />
        </Field>

        <Field id="contact-phone" label="Phone">
          <input
            id="contact-phone"
            data-testid="add-contact-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={inputClass(false)}
          />
        </Field>

        <Field id="contact-title" label="Title">
          <input
            id="contact-title"
            data-testid="add-contact-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass(false)}
          />
        </Field>

        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            data-testid="add-contact-is-primary"
            checked={is_primary}
            onChange={(e) => setIsPrimary(e.target.checked)}
          />
          Set as primary contact
        </label>

        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:bg-recessed"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="add-contact-submit"
            disabled={isSubmitting}
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? "Saving…" : "Save contact"}
          </button>
        </div>
      </form>
    </Modal>
  );
}


function Field({
  id, label, required, children,
}: {
  id: string;
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div id={id} className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-text-secondary">
        {label}
        {required ? <span className="text-rose-strong"> *</span> : null}
      </label>
      {children}
    </div>
  );
}


function inputClass(hasError: boolean): string {
  return [
    "rounded-md border bg-raised px-2.5 py-1.5 text-sm",
    hasError
      ? "border-rose-strong focus:border-rose-strong"
      : "border-border-subtle focus:border-teal",
    "focus:outline-none focus:ring-1 focus:ring-teal/40",
  ].join(" ");
}
